import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import {useEffect} from 'react'
import { AuthProvider } from "./hooks/useAuth";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";

import AuthLayout from "./components/layouts/AuthLayout";
import DashboardLayout from "./components/layouts/DashboardLayout";
import PublicLayout from "./components/layouts/PublicLayout";
import { ToastContainer } from "react-toastify";
import { pageRoute } from "./utils/common";
import Home from "./pages/home";
import TournamentDetails from "./pages/tournamentDetails";
import Channels from "./pages/channels";
import LegalDisclaimer from "./pages/dmca";
import ContactUsPage from "./pages/ContactUsPage";
import MatchDetails from "./pages/matchDetails";
import ChannelPage from "./pages/ChannelPage";
import Blogs from "./pages/blogs";
import BlogDetails from "./pages/BlogDetails";
import {
  initializeMessaging,
  generateFCMToken,
  onForegroundMessage,
} from "./firebase";
// import OneSignal from "react-onesignal";
import OneSignalSetup from "./OneSignalSetup";
import apiClient from "./utils/apiClient";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
function App() {

  const dispatch = useDispatch();

  // ========================================
  // STATES
  // ========================================

  const [loadingWebsite, setLoadingWebsite] =
    useState(true);

  const [websiteError, setWebsiteError] =
    useState(false);

  const [maintenanceMode, setMaintenanceMode] =
    useState(false);

  // ========================================
  // GET WEBSITE DATA
  // ========================================

  const getWebsiteData = async () => {
    try {

      setLoadingWebsite(true);

      setWebsiteError(false);

      const response =
        await apiClient.get(
          "/public/website"
        );

      const result = response?.data;

      // ========================================
      // SUCCESS
      // ========================================

      if (
        result?.success &&
        result?.data
      ) {

        dispatch({
          key: "website",
          value: result?.data,
          type: "SET_VALUE",
        });

        // ========================================
        // MAINTENANCE MODE
        // ========================================

        if (
          result?.data
            ?.maintenanceMode
        ) {

          setMaintenanceMode(true);

        } else {

          setMaintenanceMode(false);
        }

      } else {

        setWebsiteError(true);
      }

    } catch (error) {

      console.log(
        "Website Get Error:",
        error
      );

      setWebsiteError(true);

    } finally {

      setLoadingWebsite(false);
    }
  };

  useEffect(() => {
    getWebsiteData()
    const init = async () => {
      // INIT FIREBASE
      await initializeMessaging();

      // GENERATE TOKEN
      const token =
        await generateFCMToken();

      console.log(
        "USER TOKEN:",
        token
      );
    };

    init();

    // FOREGROUND MESSAGE
    onForegroundMessage()
      .then((payload) => {
        console.log(
          "Foreground Payload:",
          payload
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  if (loadingWebsite) {

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 3,
          background:
            "linear-gradient(to bottom, #0f172a, #111827)",
          color: "#fff",
          px: 2,
          textAlign: "center",
        }}
      >

        <CircularProgress
          size={60}
          sx={{
            color: "#fff",
          }}
        />

        <Typography
          variant="h5"
          fontWeight={700}
        >
          Loading Website...
        </Typography>

        <Typography
          variant="body2"
          sx={{
            opacity: 0.7,
            maxWidth: 400,
          }}
        >
          Please wait while we prepare
          everything for you.
        </Typography>

      </Box>
    );
  }

  // ========================================
  // ERROR SCREEN
  // ========================================

  if (websiteError) {

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          px: 2,
          textAlign: "center",
          background:
            "linear-gradient(to bottom, #111827, #000)",
          color: "#fff",
        }}
      >

        <Typography
          variant="h2"
          mb={2}
        >
          ⚠️
        </Typography>

        <Typography
          variant="h4"
          fontWeight={700}
          mb={2}
        >
          Website Temporarily Unavailable
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 500,
            opacity: 0.7,
            mb: 4,
          }}
        >
          We are having trouble
          connecting to our servers.
          Please check your internet
          connection or try again later.
        </Typography>

        <Button
          variant="contained"
          onClick={getWebsiteData}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
          }}
        >
          Retry
        </Button>

      </Box>
    );
  }

  // ========================================
  // MAINTENANCE SCREEN
  // ========================================

  if (maintenanceMode) {

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          background:
            "linear-gradient(to bottom, #0f172a, #020617)",
          color: "#fff",
          textAlign: "center",
          px: 2,
        }}
      >

        <Typography
          variant="h1"
          mb={2}
        >
          🛠️
        </Typography>

        <Typography
          variant="h3"
          fontWeight={800}
          mb={2}
        >
          Website Under Maintenance
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 600,
            opacity: 0.8,
            mb: 4,
          }}
        >
          We are currently improving
          our website to provide a
          better experience.
          Please come back again soon.
        </Typography>

        <Button
          variant="outlined"
          onClick={getWebsiteData}
          sx={{
            borderColor: "#fff",
            color: "#fff",
            px: 4,
            py: 1.5,
            borderRadius: 3,
          }}
        >
          Refresh
        </Button>

      </Box>
    );
  }

  // ========================================
  // MAIN WEBSITE
  // ========================================

  return (
    <AuthProvider>
      <OneSignalSetup /> 
      <Router>
        <Routes>
          {/* Auth Pages (Login / Signup) */}
          {/* <Route
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path={pageRoute.login} element={<>Login</>} />
          </Route> */}

          {/* Dashboard Pages */}
          {/* <Route
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route path={'/dashboard'} element={<>dashboard</>} />
          </Route> */}

          {/* Public Website Pages */}
          <Route  element={<PublicLayout />}>
            <Route path={pageRoute.blogs} element={<Blogs />} />
            <Route path={'/news/:slug'} element={<BlogDetails />} />
            <Route path={pageRoute.channels} element={<Channels />} />
            <Route path={pageRoute.dmca} element={<LegalDisclaimer />} />
            <Route path={pageRoute.contactUs} element={<ContactUsPage />} />
            <Route path={pageRoute.tournament+':slug'} element={<TournamentDetails />} />
            <Route path={pageRoute.match+':slug'} element={<MatchDetails />} />
            <Route path={pageRoute.channelPage+':slug'} element={<ChannelPage />} />
            <Route path={pageRoute.home} element={<Home />} />
          </Route>

          {/* Default Redirect */}
          <Route path="*" element={<Navigate to={'/'} replace />} />
        </Routes>
      </Router>
      <ToastContainer autoClose={1500} style={{maxWidth:'80%'}}/>
    </AuthProvider>
  );
}

export default App;

